// SEASONS (C10, APP_FORESTVOL.md §5; gs-season): the one season / snow model
// every generated look shares — CanopyGen, the near sprites, the GPU painter,
// the ring. This file is BOTH the uniform builder and the CPU reference: the
// shaders port snowWeight / formSeason line for line, and the gates diff them
// against this (APP_GENSURF §2 "every GPU pass has a CPU reference"). Pure: no
// THREE, no DOM (Node imports it).
//
//   date     tunables gensurf.season.date: 'MM-DD' (a fixed day; default
//            '09-25', the S2 measurement season — owner ruling 2026-10-22) or
//            'auto' (the device's local date) → doy 1..365 (a 365-day
//            calendar: 29 Feb reads as 28 Feb).
//   world    THE WORLD'S SEASON DATA is ONE object, SEA1 (R8i), written at
//            ingest by engine/store/world.js (seaOf) as the WORLD2 family
//            `season` (season/season.json) and read at boot (main.js) — the
//            consumers take it, never meta.bands / the vegetation block:
//              { format 'SEA1', ver, ref_doy (the measurement season's doy),
//                snow_m [lo, hi] | null (the measured snow band), species
//                {<species name>: ramp} (the species' own `season` ramps),
//                src {key: the input it came from} }
//            readSea validates it (throws: a world without one is no world,
//            main.js); seaState / applySeason / seaRamp are its readers. The
//            date + every other season key stay the owner's (tunables).
//   line     the world's MEASURED snow band, SEA1 snow_m [lo, hi] (the
//            painter's own smoothstep, surfacegen.py): line = lo, ramp = hi −
//            lo at the measurement season (SEA1 ref_doy). Away from it the
//            line descends by winter_drop_m × seasonCurve(doy): 0 from the
//            measurement day through the summer, 1 at winter_doy, a raised
//            cosine between — so the default date draws exactly the measured
//            band. No band (snow_m null): no snow. (gs-live-snow later moves
//            the line from measured daily cover instead.)
//   weight   snowWeight(state, elev, aspect, slope, catch): smoothstep(0, ramp,
//            elev + aspect_k × cos(aspect) − line) — north faces (aspect 0,
//            clockwise from grid north) hold snow aspect_k m lower — × the
//            catch (canopy snow-catch, ground 1) × catch_gain, clamped to 1, ×
//            (1 − smoothstep(shed_lo, shed_hi, slope)) — steep faces shed it.
//            Monotone in elevation by construction (check-gensurf).
//   wet      the melt season (the line rising: winter_doy → the measurement
//            day): 4 s (1 − s) × wet_gain, s = seasonCurve; 0 while it falls.
//   tile     tileSeason(state, {y0, y1}): ONE uniform set per tile, recomputed
//            when the date / snow inputs move, never per frame; `cover` = the
//            tile's upper bound (its top, a north face, flat, full catch): 0
//            lets a consumer skip the snow path for the tile.
//   ramps    formSeason(form, doy, ramp): the species' colour tint (linear
//            multiplier on its base colour) + `bare` (0..1, the larch drops
//            its needles: the canopy height falls to the branch skeleton, the
//            sprite shows its bare column). A world's species ramp (SEA1
//            species, seaRamp) overrides the form default (FORM_SEASON).
//
// Hot apply (gensched.js HOT): season.date regenerates (the canopy colour and
// the bare larch are generated); every other season key is a uniform (the
// snow is applied at draw time: moving the line never regenerates a tile).

export const SEASON_VER = 1;
// '09-25': the default date (an unreadable date resolves to it). The WORLD's
// measurement season is SEA1 ref_doy (today 268 for every world: world.js
// SEA_REF_DOY, check-gensurf pins the two equal); REF_DOY is the default of the
// pure model below for a caller that passes none (the gates' fixtures).
export const REF_DOY = 268;
const MDAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const D2R = Math.PI / 180;

// the season keys and their bounds (the client clamps; check-gensurf: the
// defaults lie inside) — generic, no world data
export const SEASON_LIM = {
  winter_doy: [1, 365], winter_drop_m: [0, 3000], aspect_k_m: [0, 500],
  shed_deg_lo: [0, 90], shed_deg_hi: [0, 90], catch_gain: [0, 2], wet_gain: [0, 2],
};

export const DATE_RE = /^(auto|(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/;

// 'MM-DD' | 'auto' (now: the device clock, injectable) → doy 1..365; an
// invalid string → REF_DOY
export function resolveDoy(date, now = new Date()) {
  let m, d;
  if (date === 'auto') { m = now.getMonth() + 1; d = now.getDate(); }
  else if (typeof date === 'string' && DATE_RE.test(date)) { m = +date.slice(0, 2); d = +date.slice(3, 5); }
  else return REF_DOY;
  d = Math.min(d, MDAYS[m - 1]);
  let doy = d;
  for (let i = 0; i < m - 1; i++) doy += MDAYS[i];
  return doy;
}

const cyc = (x) => ((x % 365) + 365) % 365;
const smooth = (lo, hi, x) => { const t = Math.min(1, Math.max(0, (x - lo) / (hi - lo))); return t * t * (3 - 2 * t); };

// 0 at the measurement day (refDoy) and through the summer, 1 at winter_doy,
// a raised cosine between
export function seasonCurve(doy, winterDoy, refDoy = REF_DOY) {
  const c = (d) => 0.5 * (1 + Math.cos(2 * Math.PI * cyc(d - winterDoy) / 365));
  const cr = c(refDoy);
  return cr >= 1 ? 0 : Math.max(0, (c(doy) - cr) / (1 - cr));
}

// the season block clamped to SEASON_LIM (a copy; unknown keys dropped); a
// missing or non-numeric key THROWS (tunables carry every key — check-gensurf)
export function clampSeason(s = {}) {
  const out = { date: typeof s.date === 'string' && DATE_RE.test(s.date) ? s.date : '09-25' };
  for (const [k, [lo, hi]] of Object.entries(SEASON_LIM)) {
    const v = s[k];
    if (typeof v !== 'number' || !Number.isFinite(v)) throw new Error(`gensurf.season.${k}: ${v} is not a number`);
    out[k] = Math.min(hi, Math.max(lo, v));
  }
  if (out.shed_deg_hi < out.shed_deg_lo + 1) out.shed_deg_hi = Math.min(90, out.shed_deg_lo + 1);
  return out;
}

// THE MODEL (pure; the CPU reference the gates pin): the world-wide state for
// a date: season = live gensurf.season, bands = {snow_m} (a consumer passes
// the SEA1 object through seaState, never meta.bands), now = the clock 'auto'
// reads, refDoy = the measurement season (seaState: SEA1 ref_doy)
export function seasonState(season, bands, now = new Date(), refDoy = REF_DOY) {
  const s = clampSeason(season);
  const doy = resolveDoy(s.date, now);
  const k = seasonCurve(doy, s.winter_doy, refDoy);
  const b = bands && Array.isArray(bands.snow_m) && bands.snow_m.length === 2 ? bands.snow_m : null;
  const melting = cyc(doy - s.winter_doy) < cyc(refDoy - s.winter_doy);
  return {
    ver: SEASON_VER, doy,
    snow_line_m: b ? b[0] - s.winter_drop_m * k : Infinity,
    snow_ramp_m: b ? Math.max(1, b[1] - b[0]) : 1,
    aspect_k: s.aspect_k_m,
    slope_shed: [s.shed_deg_lo * D2R, s.shed_deg_hi * D2R],
    catch_gain: s.catch_gain,
    wet: melting ? 4 * k * (1 - k) * s.wet_gain : 0,
  };
}

// THE texel function (the shaders port it): elev m, aspect rad (clockwise
// from grid north: 0 = a north face), slope rad, catchK 0..1 (ground: 1)
export function snowWeight(st, elev, aspect, slope, catchK = 1) {
  if (!(st.snow_line_m < Infinity)) return 0;
  const x = elev + st.aspect_k * Math.cos(aspect) - st.snow_line_m;
  const w = smooth(0, st.snow_ramp_m, x);
  const c = Math.min(1, Math.max(0, catchK * st.catch_gain));
  return w * c * (1 - smooth(st.slope_shed[0], st.slope_shed[1], slope));
}

// the per-tile uniform set: tile {y0, y1} its elevation range (m)
export function tileSeason(st, tile) {
  return { ...st, cover: snowWeight(st, tile.y1, 0, 0, 1) };
}

// uSeason as two vec4 (the GPU layout): [doy, line, ramp, aspect_k],
// [shed_lo, shed_hi, catch_gain, wet]; `cover` stays CPU-side (skip test).
// No snow: line = 1e9 (a float the shader can compare).
export function packSeason(u, out = new Float32Array(8)) {
  out[0] = u.doy; out[1] = u.snow_line_m < Infinity ? u.snow_line_m : 1e9; out[2] = u.snow_ramp_m; out[3] = u.aspect_k;
  out[4] = u.slope_shed[0]; out[5] = u.slope_shed[1]; out[6] = u.catch_gain; out[7] = u.wet;
  return out;
}

// per-form season ramps: [{doy, tint: [r, g, b] (linear multiplier on the
// species colour), bare 0..1}], cyclic, linear between keys. A form without
// one keeps its colour all year.
export const FORM_SEASON = {
  larch: [
    { doy: 120, tint: [1, 1, 1], bare: 1 },              // 30 Apr: still bare
    { doy: 145, tint: [1.15, 1.2, 1.0], bare: 0 },       // 25 May: fresh light green
    { doy: 200, tint: [1, 1, 1], bare: 0 },
    { doy: 268, tint: [1, 1, 1], bare: 0 },              // the measurement season: as measured
    { doy: 290, tint: [2.1, 1.1, 0.6], bare: 0 },        // mid-October: gold
    { doy: 305, tint: [1.9, 1.0, 0.55], bare: 1 },       // 1 Nov: bare
  ],
  broadleaf: [
    { doy: 110, tint: [1, 1, 1], bare: 1 },
    { doy: 135, tint: [1.15, 1.2, 1.0], bare: 0 },
    { doy: 268, tint: [1, 1, 1], bare: 0 },
    { doy: 290, tint: [1.8, 1.2, 0.5], bare: 0 },
    { doy: 310, tint: [1.5, 0.9, 0.5], bare: 1 },
  ],
  birch: [
    { doy: 115, tint: [1, 1, 1], bare: 1 },
    { doy: 140, tint: [1.15, 1.2, 1.0], bare: 0 },
    { doy: 268, tint: [1, 1, 1], bare: 0 },
    { doy: 285, tint: [1.9, 1.5, 0.5], bare: 0 },
    { doy: 305, tint: [1.6, 1.3, 0.5], bare: 1 },
  ],
  conifer: [
    { doy: 50, tint: [0.88, 0.92, 0.92], bare: 0 },      // mid-winter: darker, bluer needles
    { doy: 140, tint: [1, 1, 1], bare: 0 },
    { doy: 330, tint: [1, 1, 1], bare: 0 },
  ],
};

// the ramp at doy (ramp: a species' own — seaRamp — else FORM_SEASON[form])
export function formSeason(form, doy, ramp) {
  const r = Array.isArray(ramp) && ramp.length ? ramp : FORM_SEASON[form];
  if (!r || !r.length) return { tint: [1, 1, 1], bare: 0 };
  const keys = [...r].sort((a, b) => a.doy - b.doy);
  let i = keys.findIndex((k) => k.doy > doy);
  if (i < 0) i = 0;                               // past the last key: wrap to the first
  const b = keys[i], a = keys[(i - 1 + keys.length) % keys.length];
  const span = cyc(b.doy - a.doy) || 365, t = cyc(doy - a.doy) / span;
  const lerp = (x, y) => x + (y - x) * t;
  return { tint: [0, 1, 2].map((j) => lerp(a.tint[j], b.tint[j])), bare: lerp(a.bare, b.bare) };
}

// ---- SEA1: THE world's season data (R8i; header "world"). The one reader:
// readSea validates (throws on anything else — no fallback to meta), seaState
// = the model on it, seaRamp = a species' own ramp by name (null: the form's).
export const SEA_FORMAT = 'SEA1';
const num = (x) => typeof x === 'number' && Number.isFinite(x);
export function readSea(sea) {
  if (!sea || typeof sea !== 'object' || sea.format !== SEA_FORMAT) throw new Error(`season: not a ${SEA_FORMAT} object`);
  if (!(Number.isInteger(sea.ref_doy) && sea.ref_doy >= 1 && sea.ref_doy <= 365)) throw new Error(`season: SEA1 ref_doy ${sea.ref_doy}`);
  if (sea.snow_m !== null && !(Array.isArray(sea.snow_m) && sea.snow_m.length === 2 && sea.snow_m.every(num))) {
    throw new Error(`season: SEA1 snow_m ${JSON.stringify(sea.snow_m)} (null or [lo, hi])`);
  }
  if (!sea.species || typeof sea.species !== 'object' || Array.isArray(sea.species)) throw new Error('season: SEA1 species is not an object');
  return sea;
}
export function seaState(sea, season, now = new Date()) {
  readSea(sea);
  return seasonState(season, sea, now, sea.ref_doy);
}
export function seaRamp(sea, name) {
  readSea(sea);
  const r = typeof name === 'string' && Object.hasOwn(sea.species, name) ? sea.species[name] : null;
  return Array.isArray(r) && r.length ? r : null;
}

// ---- the GPU side (gs-sprite-snow): ONE port of snowWeight for every shader
// that snows (forestvol.js, the near sprites vegmat.js); `uniform vec4
// uSeason[2]` (packSeason) declared by the includer. Line for line the
// reference above (no band: line 1e9).
export const SNOW_GLSL = `
float snowWeight(float elev, float aspect, float slope, float catchK) {
  if (!(uSeason[0].y < 1e9)) return 0.0;
  float x = elev + uSeason[0].w * cos(aspect) - uSeason[0].y;
  float w = smoothstep(0.0, uSeason[0].z, x);
  float c = clamp(catchK * uSeason[1].z, 0.0, 1.0);
  return w * c * (1.0 - smoothstep(uSeason[1].x, uSeason[1].y, slope));
}
`;

// THE world season the shaders read: one uSeason object (forestvol's
// material and every sprite material hold it), packed by applySeason (the
// live season + the world's SEA1) when the live season moves (never per
// frame; 'auto' reads the clock then)
export const SEASON = {
  uSeason: { value: packSeason({ doy: REF_DOY, snow_line_m: Infinity, snow_ramp_m: 1, aspect_k: 0, slope_shed: [0, 1], catch_gain: 0, wet: 0 }) },
  doy: REF_DOY,
};
export function applySeason(season, sea, now = new Date()) {
  const st = seaState(sea, season, now);
  packSeason(st, SEASON.uSeason.value);
  SEASON.doy = st.doy;
  return st;
}

// ---- the near sprites (gs-sprite-snow; vegmat.js patchSpriteMat): the
// fragment's season on the atlas's season page (vegpaint.js seasonPage:
// R catch, G foliage) with the species' uniforms (crowns.js spriteSeason:
// tint, bare, catch): a foliage texel whose hash (the texel's interleaved
// gradient noise) is below bare drops (the larch sheds its needles, the
// wood stays), foliage takes the tint, then the snow — snowWeight at the
// fragment's world y on a crown top (aspect 0, slope 0 as the volume's) ×
// the page catch × the species catch — mixes toward SPR_SNOW (display
// colours: the sprites are painted, not lit). snowK: 1, but through the
// hand-off band 1 − hoWeight (trees.js patchHandoff: there the canopy tile's
// snow takes over, so the band never snows twice). CPU reference of
// SPR_SEASON_GLSL (snowK 1): rgb [0..1], page [R, G] 0..1, hash [0, 1).
export const SPR_SNOW = [0.86, 0.89, 0.93];
export function spriteSeasonRef(rgb, page, sp, st, y, hash) {
  const fol = page[1] >= 0.5;
  if (fol && hash < sp.bare) return { keep: false, rgb: null, snow: 0 };
  const t = rgb.map((v, i) => v * (fol ? sp.tint[i] : 1));
  const snow = snowWeight(st, y, 0, 0, page[0] * sp.catch);
  return { keep: true, rgb: t.map((v, i) => v + (SPR_SNOW[i] - v) * snow), snow };
}
export const SPR_SEASON_GLSL = `
float sprHash(vec2 t) { return fract(52.9829189 * fract(dot(floor(t), vec2(0.06711056, 0.00583715)))); }
vec3 sprSeason(vec3 rgb, vec2 uv, vec2 size, vec4 page, vec4 sp, float catchK, float y, float snowK) {
  bool fol = page.g >= 0.5;
  if (fol && sprHash(uv * size) < sp.w) discard;
  rgb *= fol ? sp.rgb : vec3(1.0);
  return mix(rgb, vec3(${SPR_SNOW.map((v) => v.toFixed(2)).join(', ')}), snowWeight(y, 0.0, 0.0, page.r * catchK) * snowK);
}
`;
